import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import './ui.css';

/**
 * A row that scrolls sideways when it doesn't fit (tabs, filter chips). When
 * there's more off to one side, that edge fades out and shows an arrow, which
 * scrolls the row when tapped. The selected item ([aria-selected=true] or
 * [aria-pressed=true]) is scrolled into view when it changes.
 *
 * Takes the same props as the <div> it draws (className, role, aria-label,
 * onKeyDown…); `selected` is whatever identifies the selected item, so the
 * row knows when to bring it into view.
 */
const ScrollRow = forwardRef(function ScrollRow({ children, selected, ...rest }, outerRef) {
  const ref = useRef(null);
  const [more, setMore] = useState({ left: false, right: false });

  const setRefs = (el) => {
    ref.current = el;
    if (typeof outerRef === 'function') outerRef(el);
    else if (outerRef) outerRef.current = el;
  };

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setMore(m => (m.left === left && m.right === right ? m : { left, right }));
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  // Bring the selected item into view, without moving the page up or down
  useEffect(() => {
    const el = ref.current;
    const item = el?.querySelector('[aria-selected="true"], [aria-pressed="true"]');
    if (!el || !item) return;
    const start = item.offsetLeft - el.offsetLeft;
    const end = start + item.offsetWidth;
    if (start < el.scrollLeft) el.scrollLeft = Math.max(0, start - 24);
    else if (end > el.scrollLeft + el.clientWidth) el.scrollLeft = end - el.clientWidth + 24;
    measure();
  }, [selected, measure]);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    const by = Math.max(120, el.clientWidth * 0.7) * dir;
    if (el.scrollBy) el.scrollBy({ left: by, behavior: 'smooth' });
    else el.scrollLeft += by;
  };

  return (
    <div className={`ui-scroll${more.left ? ' ui-scroll--left' : ''}${more.right ? ' ui-scroll--right' : ''}`}>
      <div ref={setRefs} {...rest} onScroll={measure}>
        {children}
      </div>
      {/* Keyboard and screen reader users move along the row itself */}
      {more.left && (
        <button type="button" className="ui-scroll__more ui-scroll__more--left" tabIndex={-1} aria-hidden="true" onClick={() => scroll(-1)}>
          <ChevronLeftIcon />
        </button>
      )}
      {more.right && (
        <button type="button" className="ui-scroll__more ui-scroll__more--right" tabIndex={-1} aria-hidden="true" onClick={() => scroll(1)}>
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
});

export default ScrollRow;
